import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { User, Role } from '../../types';
import { UserPlus, Edit2, Trash2, Eye, EyeOff } from 'lucide-react';
export const StaffManagement: React.FC = () => {
  const {
    users,
    addUser,
    updateUser,
    deleteUser,
    currentUser
  } = useData();
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState({
    fullName: '',
    password: '',
    role: 'Reception' as Role
  });
  const roles: {
    value: Role;
    label: string;
  }[] = [{
    value: 'Reception',
    label: 'Reception'
  }, {
    value: 'Nursing',
    label: 'Nursing'
  }, {
    value: 'Clinician',
    label: 'Clinician'
  }, {
    value: 'Laboratory',
    label: 'Laboratory'
  }, {
    value: 'Radiology',
    label: 'Radiology'
  }, {
    value: 'Pharmacy',
    label: 'Pharmacy'
  }, {
    value: 'Administrator',
    label: 'Administrator'
  }];
  const handleOpenAdd = () => {
    setEditingUser(null);
    setFormData({
      fullName: '',
      password: '',
      role: 'Reception'
    });
    setShowModal(true);
  };
  const handleOpenEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      fullName: user.fullName,
      password: user.password || '',
      role: user.role
    });
    setShowModal(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validation
    if (formData.password.length < 6 || !/\d/.test(formData.password) || !/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
      alert('Password must be at least 6 characters and include numbers and special characters.');
      return;
    }
    if (editingUser) {
      updateUser(editingUser.id, formData);
    } else {
      addUser(formData);
    }
    setShowModal(false);
  };
  const handleDelete = (id: string, role: string) => {
    if (role === 'Administrator') {
      alert('Administrator accounts cannot be deleted.');
      return;
    }
    if (id === currentUser?.id) {
      alert('You cannot delete your own account.');
      return;
    }
    if (window.confirm('Are you sure you want to delete this user?')) {
      deleteUser(id);
    }
  };
  const togglePasswordVisibility = (id: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  return <div className="space-y-6" data-id="element-563">
      <div className="flex justify-between items-center" data-id="element-564">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-565">Staff Management</h1>
        <Button onClick={handleOpenAdd} data-id="element-566">
          <UserPlus className="w-4 h-4 mr-2" data-id="element-567" /> Add New Staff
        </Button>
      </div>

      <Card noPadding data-id="element-568">
        <div className="overflow-x-auto" data-id="element-569">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-570">
            <thead className="bg-gray-50" data-id="element-571">
              <tr data-id="element-572">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-573">
                  Name / Username
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-574">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-575">
                  Password
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-576">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-577">
              {users.map(user => <tr key={user.id} data-id="element-578">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-579">
                    <div className="text-sm font-medium text-gray-900" data-id="element-580">
                      {user.fullName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-581">
                    <Badge variant={user.role === 'Administrator' ? 'danger' : 'default'} data-id="element-582">
                      {user.role}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-583">
                    <div className="flex items-center" data-id="element-584">
                      <span className="text-sm text-gray-500 font-mono mr-2" data-id="element-585">
                        {showPasswords[user.id] ? user.password : '••••••••'}
                      </span>
                      <button onClick={() => togglePasswordVisibility(user.id)} className="text-gray-400 hover:text-gray-600" data-id="element-586">
                        {showPasswords[user.id] ? <EyeOff className="w-4 h-4" data-id="element-587" /> : <Eye className="w-4 h-4" data-id="element-588" />}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-589">
                    <button onClick={() => handleOpenEdit(user)} className="text-blue-600 hover:text-blue-900 mr-4" data-id="element-590">
                      <Edit2 className="w-4 h-4" data-id="element-591" />
                    </button>
                    <button onClick={() => handleDelete(user.id, user.role)} className={`${user.role === 'Administrator' ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:text-red-900'}`} disabled={user.role === 'Administrator'} data-id="element-592">
                      <Trash2 className="w-4 h-4" data-id="element-593" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Card>

      {showModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-594">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-595">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-596">
              {editingUser ? 'Edit Staff Member' : 'Add New Staff Member'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-597">
              <Input label="Full Name (Used as Username)" required value={formData.fullName} onChange={e => setFormData({
            ...formData,
            fullName: e.target.value
          })} data-id="element-598" />
              <Select label="Role" options={roles} required value={formData.role} onChange={e => setFormData({
            ...formData,
            role: e.target.value as Role
          })} data-id="element-599" />
              <Input label="Password" type="text" required value={formData.password} onChange={e => setFormData({
            ...formData,
            password: e.target.value
          })} placeholder="Min 6 chars, 1 number, 1 special char" data-id="element-600" />
              <p className="text-xs text-gray-500" data-id="element-601">
                Password must be minimum 6 characters and include numbers and
                special characters.
              </p>

              <div className="mt-6 flex justify-end space-x-3" data-id="element-602">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} data-id="element-603">
                  Cancel
                </Button>
                <Button type="submit" data-id="element-604">
                  {editingUser ? 'Save Changes' : 'Add Staff'}
                </Button>
              </div>
            </form>
          </div>
        </div>}
    </div>;
};